import {registerTemplateGraphNode} from "#template/template-node/register"
import {curveGeometryAttributeSetLike, curveGeometryAttributeSetLikeMarker} from "#template/nodes/geometry-graph/attribute-set/attribute-set-node"
import {curveGeometryAttributeLike} from "#template/nodes/geometry-graph/attribute/attribute-node"
import type {CurveGeometryAttributeLikeNode} from "#template/nodes/geometry-graph/attribute/attribute-node"
import type {CurveGeometryAttributeSetLikeNode} from "#template/nodes/geometry-graph/attribute-set/attribute-set-node"

import type {TemplateNodeDefinition} from "#template/template-node/types"
import {getTemplateNodeReturns} from "#template/template-node/validation"

import type {IModifyTemplateGraph} from "#template/interactions"
import type {AttributeSetRecordInputParameters} from "#template/nodes/geometry-graph/attribute-set/attribute-set-record-input/geometry-attribute-set-record-input-node"
import {
    GeometryAttributeSetRecordInputNode,
    attributeSetRecordInputParametersSchema,
} from "#template/nodes/geometry-graph/attribute-set/attribute-set-record-input/geometry-attribute-set-record-input-node"

export type CurveGeometryAttributeSetRecordInputParameters = AttributeSetRecordInputParameters<
    CurveGeometryAttributeLikeNode,
    CurveGeometryAttributeSetLikeNode
>

export interface ModifyCurveGeometryAttributeSetRecordInputLikeNode {
    addRecordEntry(
        key: string,
        value: CurveGeometryAttributeLikeNode | CurveGeometryAttributeSetLikeNode | null,
        modifyTemplateGraph: IModifyTemplateGraph,
    ): void
    removeRecordEntry(key: string, modifyTemplateGraph: IModifyTemplateGraph): void
    renameRecordEntry(oldKey: string, newKey: string, modifyTemplateGraph: IModifyTemplateGraph): void
}

const definition: TemplateNodeDefinition<CurveGeometryAttributeSetRecordInputParameters> = {
    nodeClass: "CurveGeometryAttributeSetRecordInput",
    validation: {
        parameters: attributeSetRecordInputParametersSchema(curveGeometryAttributeLike, curveGeometryAttributeSetLike),
        returns: getTemplateNodeReturns(),
    },
}

@registerTemplateGraphNode
export class CurveGeometryAttributeSetRecordInput
    extends GeometryAttributeSetRecordInputNode<1, CurveGeometryAttributeLikeNode, CurveGeometryAttributeSetLikeNode>
    implements CurveGeometryAttributeSetLikeNode, ModifyCurveGeometryAttributeSetRecordInputLikeNode
{
    get [curveGeometryAttributeSetLikeMarker]() {
        return true as const
    }

    public override get definition() {
        return definition
    }

    protected override get outputIndexingDim(): 1 {
        return 1
    }
    addRecordEntry(
        key: string,
        value: CurveGeometryAttributeLikeNode | CurveGeometryAttributeSetLikeNode | null,
        modifyTemplateGraph: IModifyTemplateGraph,
    ): void {
        modifyTemplateGraph.modifyTemplateGraph(() => this.updateParameters({record: {...this.parameters.record, [key]: value}}))
    }

    removeRecordEntry(key: string, modifyTemplateGraph: IModifyTemplateGraph): void {
        modifyTemplateGraph.modifyTemplateGraph(() => {
            const newRecord = {...this.parameters.record}
            delete newRecord[key]
            this.updateParameters({record: newRecord})
        })
    }

    renameRecordEntry(oldKey: string, newKey: string, modifyTemplateGraph: IModifyTemplateGraph): void {
        modifyTemplateGraph.modifyTemplateGraph(() => {
            const newRecord = {...this.parameters.record}
            if (newRecord[oldKey] === undefined) throw new Error(`Key "${oldKey}" does not exist in record`)

            newRecord[newKey] = newRecord[oldKey]
            delete newRecord[oldKey]
            this.updateParameters({record: newRecord})
        })
    }
}
